// Helper to create a stable string key

import { useRef, useEffect, useState, useCallback } from "react";
import { UseCacheOptions, UseCacheReturn, DEFAULT_TTL, cache } from "./types";

// Simple implementation, might need improvement for complex objects/arrays
// not sure if we shouludn't just use swr
const getKeyString = (key: unknown): string => {
    if (typeof key === 'string') return key;
    if (key === null || key === undefined) return String(key);
    try {
      // Be careful with object key order issues if keys are complex objects
      return JSON.stringify(key);
    } catch (e) {
      console.error("Failed to stringify cache key:", key, e);
      return String(key); // Fallback
    }
  };
  
  
  export function useCache<T = any>(
    key: unknown, // has 
    fetcher: (() => Promise<T>) | null, // The async function to fetch data. Null disables fetching.
    options: UseCacheOptions<T> = {}
  ): UseCacheReturn<T> {
    const { ttl = DEFAULT_TTL, initialData } = options;
    const cacheKey = getKeyString(key);
  
    const isMounted = useRef(true);
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);
  
  
    const getInitialState = (): T | undefined => {
      const cachedEntry = cache.get(cacheKey);
      if (cachedEntry) {
        const isStale = Date.now() - cachedEntry.timestamp > ttl;
        if (!isStale) {
          return cachedEntry.data; // Return fresh cached data immediately
        }
        // If stale, we'll still return it but trigger validation
      }
      return initialData; // Fallback to provided initialData
    };
  
    const [data, setData] = useState<T | undefined>(getInitialState);
    const [error, setError] = useState<Error | null>(null);
    const [isValidating, setIsValidating] = useState<boolean>(false);
  
    // isLoading is true only if there's no data currently and we are fetching
    const isLoading = isValidating && !cache.has(cacheKey);
  
    // The core fetch and cache logic, wrapped in useCallback for stability
    const fetchData = useCallback(async (isRevalidation = false) => {
      if (!fetcher) {
          // If fetcher is null, do nothing
          setIsValidating(false);
          return;
      }
  
      // Don't fetch if already validating (basic deduplication for concurrent calls)
      // More robust deduplication might be needed for rapid calls
      if (!isRevalidation && isValidating) return;
  
      setIsValidating(true);
      setError(null); // Clear previous error on new validation
  
      try {
        const fetchedData = await fetcher();
  
        if (isMounted.current) {
          const now = Date.now();
          // Update cache
          cache.set(cacheKey, { data: fetchedData, timestamp: now });
          // Update state
          setData(fetchedData);
          setError(null);
        }
      } catch (err: unknown) {
          if (isMounted.current) {
              console.error(`useCache: Error fetching data for key "${cacheKey}":`, err);
              setError(err instanceof Error ? err : new Error(String(err)));
              // Optionally clear cache on error? Or keep stale data?
              // cache.delete(cacheKey); // Uncomment to clear cache on error
          }
      } finally {
        // Ensure isValidating is set to false even if component unmounted during fetch
        // but only update state if still mounted
         if (isMounted.current) {
             setIsValidating(false);
         }
      }
    }, [cacheKey, fetcher, isValidating]); // Include isValidating to prevent concurrent fetches easily
  
    // Effect to trigger fetch on mount, key change, or if data becomes stale
    useEffect(() => {
      if (!fetcher) {
          // No fetcher provided, ensure loading states are false
          setIsValidating(false);
          setData(initialData); // Reset to initialData if fetcher becomes null
          setError(null);
          return;
      }
  
      const cachedEntry = cache.get(cacheKey);
      let shouldFetch = true; // Assume fetch is needed
  
      if (cachedEntry) {
        const isStale = Date.now() - cachedEntry.timestamp > ttl;
        if (!isStale) {
          // Data is fresh, no immediate fetch needed
          // Update state just in case it drifted (e.g., initialData was used)
          if (data !== cachedEntry.data) {
              setData(cachedEntry.data);
          }
          setError(null); // Clear any previous error if fresh data found
          setIsValidating(false); // Ensure validation state is correct
          shouldFetch = false;
        } else {
          // Data is stale, keep showing it but trigger revalidation
          // State update happens here if stale data wasn't the initial state
           if (data !== cachedEntry.data) {
              setData(cachedEntry.data);
           }
           setError(null); // Clear error when showing stale data
           // `fetchData` will be called below, setting isValidating
        }
      } else if (initialData !== undefined && data === undefined) {
          // If initialData was provided but no cache exists yet, set it
          setData(initialData);
          // Still proceed to fetch unless configured otherwise
      }
  
  
      if (shouldFetch) {
        // Check if we have *any* data (stale or initial) before deciding initial load state
        const isInitialLoad = data === undefined;
        if (!isInitialLoad) {
            // This is a revalidation (stale cache or manual mutate)
            fetchData(true); // Pass true to indicate it's a revalidation
        } else {
            // This is an initial load (no cache, no initial data shown yet)
            fetchData(false);
        }
      }
  
      // This effect depends on the stringified key and the fetcher function identity
    }, [cacheKey, fetcher, ttl, initialData]); // Rerun if key, fetcher, ttl, or initialData changes
  
  
    // Manual mutation/revalidation function
    const mutate = useCallback(async (newData?: T | Promise<T>): Promise<T | undefined> => {
      if (!fetcher) {
          console.warn("useCache: Cannot mutate when fetcher is null.");
          return undefined;
      }
  
      if (newData !== undefined) {
        // Optimistic update or direct cache setting
        try {
          const dataToCache = await Promise.resolve(newData); // Handle promise or direct value
  
          if (isMounted.current) {
            // Update local state immediately
            setData(dataToCache);
            setError(null); // Clear error on optimistic update
            // Update cache
            cache.set(cacheKey, { data: dataToCache, timestamp: Date.now() });
          }
          // Optionally trigger a background revalidation after optimistic update?
          // fetchData(true); // Uncomment to revalidate after setting data
          return dataToCache;
        } catch (err) {
          // Handle error if newData was a rejected promise
          console.error("useCache: Error resolving newData for mutate:", err);
          if (isMounted.current) {
            setError(err instanceof Error ? err : new Error(String(err)));
          }
          return undefined;
        }
  
      } else {
        // Trigger a revalidation without optimistic update
        return fetchData(true).then(() => {
          // Return the data from the cache after revalidation completes
          return cache.get(cacheKey)?.data;
        });
      }
    }, [cacheKey, fetcher, fetchData]); // Depends on cacheKey and fetcher identity
  
    return { data, error, isLoading, isValidating, mutate };
  }